import {
    Component,
    ElementRef,
    InputSignal,
    OnDestroy,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    IDatiqMapGeoJson,
    GeographyTypes,
    AreaPayload,
    MciMapDataPayload,
    MciMapDataPayloadSchema,
    MciRatio,
    McQuery,
    McLocationCoordinates,
} from '../models';
import {
    IqMcMapLoaderService,
    IqMcMapUtilsService,
    MciMapService,
} from '../state';
import { GoogleMapsApiConfig, MAPS_API_CONFIG } from '../state/services/models';
import {
    IIqMcXmaModalData,
    IqMcModalExtendedMarketComponent,
} from '@dat/iq-mc-modal';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { isEqual, isNil, omit } from 'lodash';

@Component({
    selector: 'iq-mc-map-mci',
    standalone: true,
    imports: [CommonModule, IqMcModalExtendedMarketComponent],
    templateUrl: './iq-mc-map-mci.component.html',
    styleUrl: './iq-mc-map-mci.component.scss',
    providers: [
        {
            provide: IqMcMapLoaderService,
            useClass: IqMcMapLoaderService,
        },
        {
            provide: MAPS_API_CONFIG,
            useClass: GoogleMapsApiConfig,
        },
    ],
})
export class IqMcMapMciComponent implements OnDestroy {
    // #region Service Injection
    private _loader: IqMcMapLoaderService = inject(IqMcMapLoaderService);
    private _mci: MciMapService = inject(MciMapService);
    private _ref: ElementRef = inject(ElementRef);
    private _utils: IqMcMapUtilsService = inject(IqMcMapUtilsService);
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    // #endregion

    //#region Signals
    id: InputSignal<string> = input('');
    map: WritableSignal<google.maps.Map> = signal(null);

    xmaGeographiesInPoint: WritableSignal<IIqMcXmaModalData[]> = signal([]);

    dataPayload: WritableSignal<MciMapDataPayload> = signal(null);
    areaPayload: WritableSignal<AreaPayload> = signal(null);
    locationCoords: WritableSignal<McLocationCoordinates> = signal({
        lat: null,
        lng: null,
    });

    query: Signal<McQuery> = computed(
        () => {
            return this.generateMcQueryFromMciPayload(this.dataPayload());
        },
        { equal: this.isQueryEqual },
    );

    geoCode: Signal<string> = computed(
        () => {
            return this.dataPayload()?.geoCode ?? '';
        },
        { equal: isEqual },
    );

    mciScoreMap: Signal<MciRatio[]> = computed(
        () => {
            return this.dataPayload()?.mciScores ?? [];
        },
        { equal: isEqual },
    );

    featureCollection: Signal<IDatiqMapGeoJson> = computed(
        () => {
            return this.areaPayload()?.featureCollection ?? {};
        },
        { equal: isEqual },
    );

    computedFeatures: Signal<IDatiqMapGeoJson> = computed(
        () => {
            const featureCollection: IDatiqMapGeoJson =
                this.featureCollection();
            const mciScoreMap: MciRatio[] = this.mciScoreMap();
            return this._mci.computeFeatures(mciScoreMap, featureCollection);
        },
        { equal: isEqual },
    );

    loaded: Signal<boolean> = computed(() => {
        const dataPayload = this.dataPayload();
        const featurePayload = this.areaPayload();

        return dataPayload?.loaded && featurePayload?.loaded;
    });

    hasError: Signal<boolean> = computed(() => {
        const dataPayload = this.dataPayload();
        const featurePayload = this.areaPayload();

        return dataPayload?.hasError || featurePayload?.hasError;
    });

    // #endregion

    xmaModalState: boolean = false;

    shouldClearLocationInput: boolean = false;

    triggerXmaModal: boolean = false;

    private _deregisterMap: Record<string, IqEventBusBrokerHandler> = {};

    constructor() {
        this._deregisterMap = this._bus.registerMap({
            updateMcMciMapData: {
                callback: (payload: MciMapDataPayload) => {
                    if (this.id() !== payload.id) return;
                    const validatePayload: any =
                        MciMapDataPayloadSchema.safeParse(payload);
                    if (!validatePayload.success) {
                        console.error('Invalid payload');
                        console.log(validatePayload?.error?.issues);
                        return;
                    }
                    this.triggerXmaModal =
                        this.isOnlyGeoTypeChangedForMcQuery(payload);
                    this.dataPayload.set(payload);
                    // const loads = this.mapLoads(payload.data);
                },
            },
            updateMcMciAreaData: {
                callback: (payload: AreaPayload) => {
                    if (this.id() !== payload.id) return;
                    this.areaPayload.set(payload);
                },
            },
            updateLocationCoords: {
                callback: (payload: {
                    id: string;
                    lat: number;
                    lng: number;
                }) => {
                    if (this.id() !== payload.id) return;
                    this.locationCoords.set({
                        lat: payload.lat,
                        lng: payload.lng,
                    });
                },
            },
        });

        effect(async () => {
            if (!this.loaded()) return;
            const computedFeatures: IDatiqMapGeoJson = this.computedFeatures();
            const query: McQuery = this.query();
            untracked(async () => {
                const map: google.maps.Map = await this._loader.loadMap(
                    this._ref,
                );
                this.map.set(map);

                this._mci.setMapFeatures(map, computedFeatures);
                this.setMapInteractions(map, query, computedFeatures);
                if (this.triggerXmaModal) {
                    this.openXmaModalFromLocation(
                        this.locationCoords(),
                        computedFeatures,
                    );
                    this.triggerXmaModal = false;
                }
            });
        });

        effect(() => {
            const map: google.maps.Map = this.map();
            const geoCode: string = this.geoCode();
            if (isNil(map)) return;
            this._utils.highlightSelectedFeature(map, geoCode);
        });

        effect(() => {
            const point: McLocationCoordinates = this.locationCoords();
            untracked(() => {
                if (!this.loaded()) return;
                if (isNil(point.lat) || isNil(point.lng)) return;
                const computedFeatures = this.computedFeatures();
                const geoType = this.query()?.geoType;
                if (
                    geoType &&
                    geoType.length > 0 &&
                    geoType === GeographyTypes.ExtendedMarketArea
                ) {
                    this.openXmaModalFromLocation(point, computedFeatures);
                }
            });
        });
    }

    openXmaModalFromLocation(
        point: McLocationCoordinates,
        computedFeatures: IDatiqMapGeoJson,
    ): void {
        const geometriesInPoint: IIqMcXmaModalData[] =
            this._utils.getGeometriesContaingPoint(point, computedFeatures);
        if (!isNil(geometriesInPoint) && geometriesInPoint.length > 1) {
            this.xmaGeographiesInPoint.set(geometriesInPoint);
            this.toggleXmaModal(true);
            return;
        }
    }

    toggleXmaModal(state: boolean) {
        this.xmaModalState = state;
    }

    setMapInteractions(
        map: google.maps.Map,
        query: McQuery,
        computedFeatures: IDatiqMapGeoJson,
    ) {
        map.data.addListener('click', (event: google.maps.Data.MouseEvent) => {
            if (query.geoType === GeographyTypes.ExtendedMarketArea) {
                const geometriesInPoint: IIqMcXmaModalData[] =
                    this._utils.getGeometriesFromClickEvent(
                        event,
                        computedFeatures,
                    );
                if (!isNil(geometriesInPoint) && geometriesInPoint.length > 1) {
                    this.xmaGeographiesInPoint.set(geometriesInPoint);
                    this.toggleXmaModal(true);
                    this.shouldClearLocationInput = true;
                    return;
                }
            }
            const geoCode: string = event.feature.getProperty('code');
            const zoom = map.getZoom();
            this.dispatchControlValue();
            this.dispatchRoute(geoCode, query);
            this.dispatchSegmentEvent(geoCode, zoom);
            this._utils.hightlightSelectedFeatureFromEvent(map, event);
        });
    }

    dispatchRoute(geoCode: string, query: McQuery) {
        this._bus.dispatch({
            name: 'type:mc-controls:change',
            payload: {
                query: {
                    ...query,
                    geoCode,
                },
            },
        });
    }

    dispatchControlValue() {
        this._bus.dispatch({
            name: 'setDashboardControlValue',
            payload: {
                dashboardControlId: `${this.query().id}-search-controls-bar`,
                controlName: 'geography',
                value: '',
            },
        });
    }

    dispatchSegmentEvent(geoCode: string, zoomLevel: number) {
        this._bus.dispatch({
            name: 'type:tracking:log',
            payload: {
                type: 'SEGMENT',
                eventType: 'TRACK',
                eventName: 'Clicked Shape',
                interaction: 'click',
                props: {
                    geo_code: geoCode,
                    zoom_level: zoomLevel,
                },
            },
        });
    }

    isQueryEqual(prev: McQuery, curr: McQuery): boolean {
        const prevWithoutGeography: McQuery = {
            ...prev,
            geoCode: '',
            displayName: '',
        };
        const currWithoutGeography: McQuery = {
            ...curr,
            geoCode: '',
            displayName: '',
        };
        return isEqual(prevWithoutGeography, currWithoutGeography);
    }

    selectedMarket(_geography: IIqMcXmaModalData): void {
        if (this.shouldClearLocationInput) {
            this.dispatchControlValue();
            this.shouldClearLocationInput = false;
        }
    }

    generateMcQueryFromMciPayload(payload: MciMapDataPayload | null): McQuery {
        return {
            id: payload?.dashboardId,
            type: 'area',
            geoCode: String(payload?.geoCode ?? ''),
            equipmentType: payload?.equipmentType,
            geoType: payload?.geoType,
            timeframe: payload?.timeframe,
            direction: payload?.direction,
            displayName: '',
            daysForward: String(payload?.daysForward ?? ''),
        };
    }

    isOnlyGeoTypeChangedForMcQuery(payload: MciMapDataPayload): boolean {
        if (
            payload.geoType === GeographyTypes.ExtendedMarketArea &&
            !isNil(payload.displayName)
        ) {
            const prevQuery: McQuery = this.query();
            const currQuery: McQuery =
                this.generateMcQueryFromMciPayload(payload);
            return isEqual(
                omit(prevQuery, ['geoType', 'geoCode']),
                omit(currQuery, ['geoType', 'geoCode']),
            );
        }
        return false;
    }

    ngOnDestroy(): void {
        this.map.set(null);
        this._bus.unregisterMap(this._deregisterMap);
    }
}
